import { FormEvent, useCallback, useEffect, useState } from 'react';
import { mailApi, type MailAccount, type MailAccountInput } from '../lib/mailApi';

const IONOS_DEFAULTS: MailAccountInput = {
  label: 'IONOS mailbox',
  smtpHost: 'smtp.ionos.com',
  smtpPort: 587,
  smtpSecure: false,
  imapHost: 'imap.ionos.com',
  imapPort: 993,
  dailyLimit: 30,
};

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

function warmupDay(a: MailAccount): number {
  return Math.floor((Date.now() - new Date(a.warmupStartedAt).getTime()) / 86_400_000) + 1;
}

function MailboxForm({
  initial,
  isNew,
  onSaved,
  onCancel,
}: {
  initial: MailAccountInput;
  isNew: boolean;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<MailAccountInput>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [advanced, setAdvanced] = useState(false);
  const set = <K extends keyof MailAccountInput>(k: K, v: MailAccountInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = { ...form, username: form.username || form.fromEmail };
      if (!body.password) delete body.password;
      if (isNew) await mailApi.createAccount(body);
      else await mailApi.updateAccount((initial as { id: string } & MailAccountInput).id, body);
      onSaved();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card space-y-4">
      <h3 className="font-bold text-ink">{isNew ? 'Connect a mailbox' : `Edit ${initial.fromEmail}`}</h3>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="label">Your name (shown as sender)</label>
          <input className="input" required value={form.fromName ?? ''} onChange={(e) => set('fromName', e.target.value)} placeholder="Umer Naseer" />
        </div>
        <div>
          <label className="label">Email address</label>
          <input className="input" type="email" required disabled={!isNew} value={form.fromEmail ?? ''} onChange={(e) => set('fromEmail', e.target.value)} placeholder="info@360techive.com" />
        </div>
        <div>
          <label className="label">{isNew ? 'Mailbox password' : 'New password (leave empty to keep)'}</label>
          <input className="input" type="password" autoComplete="new-password" required={isNew} value={form.password ?? ''} onChange={(e) => set('password', e.target.value)} />
          <p className="mt-1 text-xs text-slate-400">Stored encrypted. Never shown again.</p>
        </div>
        <div>
          <label className="label">Max emails per day (after warm-up)</label>
          <input className="input" type="number" min={1} max={200} value={form.dailyLimit ?? 30} onChange={(e) => set('dailyLimit', Number(e.target.value))} />
          <p className="mt-1 text-xs text-slate-400">Warm-up: 10/day in week 1, 20/day in week 2, then this number. Keep it at 30 or below for one mailbox.</p>
        </div>
        <div className="md:col-span-2">
          <label className="label">Signature</label>
          <textarea className="input min-h-[80px]" value={form.signature ?? ''} onChange={(e) => set('signature', e.target.value)} placeholder={'Umer Naseer\n360techive · Web, mobile & AI development\nhttps://360techive.com'} />
        </div>
        <div className="md:col-span-2">
          <label className="label">Postal address (required by law in every cold email)</label>
          <input className="input" value={form.footerAddress ?? ''} onChange={(e) => set('footerAddress', e.target.value)} placeholder="360techive, street, city, postcode, country" />
        </div>
      </div>

      <button type="button" className="text-xs font-semibold text-slate-500 hover:text-ink" onClick={() => setAdvanced((v) => !v)}>
        {advanced ? '▾' : '▸'} Server settings ({form.smtpHost}:{form.smtpPort} / {form.imapHost}:{form.imapPort})
      </button>
      {advanced && (
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="label">SMTP host</label>
            <input className="input" value={form.smtpHost ?? ''} onChange={(e) => set('smtpHost', e.target.value)} />
          </div>
          <div>
            <label className="label">SMTP port</label>
            <input className="input" type="number" value={form.smtpPort ?? 587} onChange={(e) => { const p = Number(e.target.value); setForm((f) => ({ ...f, smtpPort: p, smtpSecure: p === 465 })); }} />
          </div>
          <div>
            <label className="label">Login username</label>
            <input className="input" value={form.username ?? ''} onChange={(e) => set('username', e.target.value)} placeholder="same as email" />
          </div>
          <div>
            <label className="label">IMAP host</label>
            <input className="input" value={form.imapHost ?? ''} onChange={(e) => set('imapHost', e.target.value)} />
          </div>
          <div>
            <label className="label">IMAP port</label>
            <input className="input" type="number" value={form.imapPort ?? 993} onChange={(e) => set('imapPort', Number(e.target.value))} />
          </div>
        </div>
      )}

      {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-poor">{error}</p>}
      <div className="flex gap-3">
        <button className="btn-primary" disabled={busy}>{busy ? 'Checking login…' : isNew ? 'Connect mailbox' : 'Save'}</button>
        <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function SuppressionList() {
  const [rows, setRows] = useState<{ email: string; reason: string; createdAt: string }[]>([]);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => void mailApi.suppressions().then(setRows).catch(() => undefined), []);
  useEffect(load, [load]);

  return (
    <div className="card space-y-4">
      <div>
        <h3 className="font-bold text-ink">Do-not-contact list</h3>
        <p className="text-sm text-slate-500">Unsubscribes and bounces land here automatically. No campaign will ever email these addresses.</p>
      </div>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          mailApi.addSuppression(email).then(() => { setEmail(''); load(); }).catch((err) => setError(errMsg(err)));
        }}
      >
        <input className="input" type="email" required placeholder="someone@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button className="btn-ghost whitespace-nowrap">Add</button>
      </form>
      {error && <p className="text-sm text-poor">{error}</p>}
      <div className="max-h-72 overflow-auto">
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r) => (
              <tr key={r.email} className="border-t border-line">
                <td className="py-2">{r.email}</td>
                <td className="py-2 text-slate-500">{r.reason}</td>
                <td className="py-2 text-slate-400">{new Date(r.createdAt).toLocaleDateString()}</td>
                <td className="py-2 text-right">
                  <button className="text-xs text-slate-400 hover:text-poor" onClick={() => void mailApi.removeSuppression(r.email).then(load)}>Remove</button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td className="py-3 text-slate-400">Empty.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SettingsPage() {
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  const [editing, setEditing] = useState<'new' | MailAccount | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const load = useCallback(() => void mailApi.accounts().then(setAccounts).catch(() => undefined), []);
  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setNotice(null);
    try {
      await fn();
      setNotice(ok);
      load();
    } catch (err) {
      setNotice(errMsg(err));
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-6 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-ink">Mailboxes</h2>
          <p className="text-sm text-slate-500">Campaigns send through your own mailbox and read replies from its inbox. Nothing is marked as read.</p>
        </div>
        {!editing && <button className="btn-primary" onClick={() => setEditing('new')}>Connect mailbox</button>}
      </div>

      {notice && <p className="rounded-lg border border-line bg-white p-3 text-sm">{notice}</p>}

      {editing && (
        <MailboxForm
          isNew={editing === 'new'}
          initial={editing === 'new' ? IONOS_DEFAULTS : { ...editing, password: '' }}
          onSaved={() => { setEditing(null); load(); }}
          onCancel={() => setEditing(null)}
        />
      )}

      {accounts.map((a) => (
        <div key={a.id} className="card space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-bold text-ink">{a.fromName} &lt;{a.fromEmail}&gt;</div>
              <div className="text-xs text-slate-400">
                {a.smtpHost}:{a.smtpPort} · inbox checked {a.lastCheckedAt ? new Date(a.lastCheckedAt).toLocaleTimeString() : 'not yet'}
                {a.sentFolder ? ` · copies saved to “${a.sentFolder}”` : ''}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button className="btn-ghost !py-1.5 text-xs" onClick={() => void act(() => mailApi.testAccount(a.id), `Test email sent to ${a.fromEmail}. Check that inbox.`)}>Send test email</button>
              <button className="btn-ghost !py-1.5 text-xs" onClick={() => setEditing(a)}>Edit</button>
              <button className="btn-ghost !py-1.5 text-xs" onClick={() => void act(() => mailApi.updateAccount(a.id, { active: !a.active }), a.active ? 'Mailbox deactivated.' : 'Mailbox activated.')}>
                {a.active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <div className="rounded-lg bg-canvas p-3"><div className="text-lg font-extrabold">{a.sentToday} / {a.todayLimit}</div><div className="text-xs text-slate-500">sent today / today's limit</div></div>
            <div className="rounded-lg bg-canvas p-3"><div className="text-lg font-extrabold">Day {warmupDay(a)}</div><div className="text-xs text-slate-500">{warmupDay(a) <= 14 ? 'warming up' : 'warm-up done'}</div></div>
            <div className="rounded-lg bg-canvas p-3"><div className="text-lg font-extrabold">{a.sent7d}</div><div className="text-xs text-slate-500">sent, last 7 days</div></div>
            <div className="rounded-lg bg-canvas p-3"><div className={`text-lg font-extrabold ${a.sent7d && a.bounced7d / a.sent7d > 0.03 ? 'text-poor' : ''}`}>{a.bounced7d}</div><div className="text-xs text-slate-500">bounced, last 7 days</div></div>
          </div>

          {!a.footerAddress && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">Add your postal address (Edit). Campaigns can't start without it.</p>}
          {a.pausedReason && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-red-50 p-3 text-sm text-poor">
              <span>Paused: {a.pausedReason}</span>
              <button className="btn-ghost !py-1 text-xs" onClick={() => void act(() => mailApi.updateAccount(a.id, { resume: true }), 'Sending resumed.')}>Resume sending</button>
            </div>
          )}
          {a.lastError && !a.pausedReason && <p className="text-xs text-slate-500">Last error: {a.lastError}</p>}
        </div>
      ))}

      {!accounts.length && !editing && (
        <div className="card text-sm text-slate-500">No mailbox yet. Connect your IONOS mailbox to start sending campaigns.</div>
      )}

      <SuppressionList />
    </div>
  );
}
